import React from 'react';
import axios from 'axios';
import { Box, Typography, Card, CardContent, Button, TextField, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, LinearProgress, Chip, Alert, Divider } from '@mui/material';
import { Science as ScienceIcon, PlayArrow as PlayArrowIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon, UnfoldMore as UnfoldMoreIcon } from '@mui/icons-material';
import { tableStyles } from '../reportingStyles';
import { useReporting } from '../ReportingContext';

const RevShareTestTab = () => {
  const {
    revShareRunning,
    revShareResult,
    revShareError,
    revSharePayout,
    revShareCleaning,
    setRevSharePayout,
    runRevShareTest,
    cleanupRevShareTest,
    clearAllReportingData,
    runSimpleVerificationTest,
    sortColumn,
    sortDirection,
    blogSortColumn,
    blogSortDirection,
    snakeSortColumn,
    snakeSortDirection,
    snakeAdminRevExpanded,
    setSortColumn,
    setSortDirection,
    setBlogSortColumn,
    setBlogSortDirection,
    setSnakeSortColumn,
    setSnakeSortDirection,
    setSnakeAdminRevExpanded,
    fetchOfferStats,
    setRevShareResult,
    setRevShareError,
    setActiveTab,
  } = useReporting();

  return (
  <Box>
    {/* Header */}
    <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)', color: 'white' }}>
      <CardContent sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <ScienceIcon sx={{ fontSize: 36, opacity: 0.9 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
              RevShare Logic Tester
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              Simulate head &amp; snake blog conversions and verify the 70 / 40 / 30 revenue split
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>

    {/* Controls */}
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Simulation Parameters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="Payout per conversion ($)"
              type="number"
              size="small"
              fullWidth
              value={revSharePayout}
              onChange={e => setRevSharePayout(e.target.value)}
              inputProps={{ min: 0.01, step: 0.01 }}
              helperText="Applied to both head and snake conversions"
            />
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                id="run-revshare-test-btn"
                variant="contained"
                size="large"
                startIcon={revShareRunning ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                onClick={runRevShareTest}
                disabled={revShareRunning}
                sx={{
                  background: 'linear-gradient(135deg, #1a237e, #3949ab)',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': { background: 'linear-gradient(135deg, #283593, #3f51b5)' }
                }}
              >
                {revShareRunning ? 'Running Simulation…' : 'Run Simulation'}
              </Button>
              <Button
                variant="contained"
                color="success"
                size="large"
                startIcon={<CheckCircleIcon />}
                onClick={runSimpleVerificationTest}
                disabled={revShareRunning}
                sx={{ fontWeight: 600, px: 3 }}
              >
                Simple Verification
              </Button>
              {revShareResult && (
                <Button
                  variant="outlined"
                  onClick={() => { setRevShareResult(null); setRevShareError(null); }}
                  sx={{ fontWeight: 600 }}
                >
                  Reset
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                onClick={clearAllReportingData}
                disabled={revShareCleaning || revShareRunning}
                sx={{ fontWeight: 600, ml: 'auto' }}
              >
                {revShareCleaning ? 'Clearing...' : 'Clear All Reporting Data'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>

    {/* Error */}
    {revShareError && (
      <Alert severity="error" sx={{ mb: 3 }} icon={<ErrorIcon />}>
        <strong>Simulation Error:</strong> {revShareError}
      </Alert>
    )}

    {/* Results */}
    {revShareResult && (() => {
      const r = revShareResult;
      return (
        <Box>
          {/* Live Data Banner */}
          {r.dataInDb && (
            <Paper sx={{
              p: 3, mb: 3,
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid #10b981',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  bgcolor: '#10b981', color: 'white', p: 1, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <CheckCircleIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#065f46' }}>
                    Test data is now LIVE in the database
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#065f46', opacity: 0.8 }}>
                    Revenue from this simulation has been recorded. You can verify it in the stats tabs below.
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setActiveTab(2)}
                  sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                >
                  View Blog Stats
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setActiveTab(3)}
                  sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
                >
                  View Snake Stats
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                >
                  {revShareCleaning ? 'Cleaning...' : 'Clean Up Now'}
                </Button>
              </Box>
            </Paper>
          )}

          {/* Config summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Test Configuration</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f3f4f6', textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Offer Used</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {r.config.offer.title}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#fff3e0', textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Payout / Conversion</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: '#e65100' }}>
                      ${r.config.payoutPerConversion.toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={12} md={4}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#e3f2fd', textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Affiliates Involved</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {Object.keys(r.summary.affiliateEarnings).length} active participants
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Conversion breakdowns */}
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, ml: 1 }}>Simulated Conversions ({r.conversions.length})</Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {r.conversions.map((conv, idx) => (
              <Grid item xs={12} md={6} key={conv.id || idx}>
                <Card sx={{ height: '100%', border: conv.type === 'snake' ? '2px solid #fbbf24' : '2px solid #10b981' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '50%',
                          bgcolor: conv.type === 'snake' ? '#f59e0b' : '#10b981'
                        }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{conv.label}</Typography>
                      </Box>
                      <Chip label={conv.type.toUpperCase()} size="small" color={conv.type === 'snake' ? "warning" : "success"} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {conv.description}
                    </Typography>
                    <Box sx={{ bgcolor: '#f9fafb', p: 1, borderRadius: 1, mb: 2 }}>
                      {conv.type === 'head' ? (
                        <>
                          <Typography variant="caption" color="text.secondary" display="block">Head Blog Used</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{conv.headBlogTitle}</Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="caption" color="text.secondary" display="block">Head Blog Used</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{conv.headBlogTitle}</Typography>
                          
                          <Typography variant="caption" color="text.secondary" display="block">Converted Blog within Snake</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#f59e0b' }}>{conv.snakeBlogTitle}</Typography>
                        </>
                      )}
                    </Box>
                    
                    <Divider sx={{ mb: 2 }} />

                    {/* Breakdown rows */}
                    {conv.breakdown.map((item, bIdx) => (
                      <Box key={bIdx} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {item.party}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{item.role}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body1" sx={{ fontWeight: 700, color: item.color }}>
                              ${item.amount.toFixed(2)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {((item.amount / conv.payout) * 100).toFixed(0)}%
                            </Typography>
                          </Box>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(item.amount / conv.payout) * 100}
                          sx={{
                            height: 8, borderRadius: 4,
                            bgcolor: '#f3f4f6',
                            '& .MuiLinearProgress-bar': { bgcolor: item.color, borderRadius: 4 }
                          }}
                        />
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Summary table */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Combined Earnings Summary</Typography>
              <TableContainer component={Paper} sx={{ ...tableStyles.container }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={tableStyles.headerRow}>
                      <TableCell sx={tableStyles.headerCell}>Participant</TableCell>
                      <TableCell sx={tableStyles.headerCell}>Total Role(s)</TableCell>
                      <TableCell align="right" sx={tableStyles.headerCell}>Total Earnings</TableCell>
                      <TableCell align="right" sx={tableStyles.headerCell}>% of Gross</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(r.summary.affiliateEarnings).map(([name, amount], idx) => (
                      <TableRow key={idx} sx={tableStyles.bodyRow}>
                        <TableCell sx={tableStyles.bodyCell}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: idx % 2 === 0 ? '#4CAF50' : '#2196F3' }} />
                            <strong>{name}</strong>
                          </Box>
                        </TableCell>
                        <TableCell sx={tableStyles.bodyCell}>
                          <Chip 
                            label={r.conversions.some(c => c.tp === name) ? "Traffic Provider" : "Content Creator"} 
                            size="small" 
                            variant="outlined" 
                          />
                          {r.conversions.some(c => c.tp === name) && r.conversions.some(c => c.cc === name) && (
                            <Chip label="Dual Role" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ ...tableStyles.bodyCell, fontWeight: 700, color: '#166534' }}>
                          ${amount.toFixed(2)}
                        </TableCell>
                        <TableCell align="right" sx={tableStyles.bodyCell}>
                          {((amount / r.summary.totalGrossPayout) * 100).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Platform row */}
                    <TableRow sx={{ ...tableStyles.bodyRow, bgcolor: '#f9fafb' }}>
                      <TableCell sx={tableStyles.bodyCell}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#9E9E9E' }} />
                          <strong>Platform (ESX)</strong>
                        </Box>
                      </TableCell>
                      <TableCell sx={tableStyles.bodyCell}>
                        <Chip label="Platform Fee" size="small" sx={{ bgcolor: '#f3f4f6', color: '#374151' }} />
                      </TableCell>
                      <TableCell align="right" sx={{ ...tableStyles.bodyCell, fontWeight: 700 }}>
                        ${r.summary.platformEarnings.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={tableStyles.bodyCell}>
                        {((r.summary.platformEarnings / r.summary.totalGrossPayout) * 100).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                    {/* Grand Total Row */}
                    <TableRow>
                      <TableCell colSpan={2} align="right" sx={{ fontWeight: 700, py: 2 }}>Gross Simulation Revenue:</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '1.1rem', py: 2 }}>
                        ${r.summary.totalGrossPayout.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, py: 2 }}>100%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Step-by-step log */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Simulation Log</Typography>
              <Box sx={{
                bgcolor: '#0f172a', borderRadius: 2, p: 2,
                maxHeight: 360, overflowY: 'auto',
                fontFamily: 'monospace'
              }}>
                {r.log.map((entry, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ color: '#64748b', fontSize: '0.72rem', whiteSpace: 'nowrap', mt: '1px' }}>
                      {new Date(entry.ts).toLocaleTimeString()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#e2e8f0' }}>
                      {entry.emoji} {entry.msg}
                      {entry.data && (
                        <Box component="span" sx={{ color: '#94a3b8', ml: 1, fontSize: '0.75rem' }}>
                          {JSON.stringify(entry.data).slice(0, 120)}
                        </Box>
                      )}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 16 }} />
                  <Typography sx={{ color: '#22c55e', fontSize: '0.85rem', fontWeight: 600 }}>
                    Simulation complete — test data is now LIVE in reporting tabs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    })()}

    {/* Instructions when no result */}
    {!revShareResult && !revShareRunning && !revShareError && (
      <Card sx={{ bgcolor: '#f8fafc' }}>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <ScienceIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>Ready to Simulate</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
            Set the payout amount above and click <strong>Run Simulation</strong>. The test will pick two
            active affiliates from the database, simulate a head blog conversion and a snake blog conversion,
            then display the exact revshare split — all without touching your production data.
          </Typography>
        </CardContent>
      </Card>
    )}
  </Box>

  );
};

export default RevShareTestTab;
